import type { AuthorizationRouteSummary } from '@/services/authorization-case.service';

/**
 * Stage 5F — the Authorization Center's per-row expected-route cell.
 *
 * ── IT RENDERS, IT DOES NOT DERIVE ──────────────────────────────────────────
 * Every value here is computed **server-side** inside the aggregate
 * (design §12.2): *"the client must never re-derive them, or two answers would
 * exist."* This component performs no comparison, reads no `trail`, and knows
 * nothing about `status` or `workflowStage`. It is a formatter.
 *
 * ── §12.3, THE COMPACT-STRIP HAZARD ─────────────────────────────────────────
 * The design warns that a row rendering `OPS · COMMERCIAL · ADMIN · ENGINEER`
 * *"reads as a pipeline even with no arrows"* and instructs Stage 5 to
 * **inherit Stage 3's legend/trail separation rather than invent a compact
 * single-row diagram**. So this cell draws **no sequence of any kind** — no
 * chevrons, no arrows, no ordered owner strip, no step list. One status word,
 * plus counts that are plainly labelled quantities.
 *
 * ── D-112 OD-R1 ─────────────────────────────────────────────────────────────
 * Nothing here names a future stage. `IN_PROGRESS` says no configured step has
 * been reached yet; it never says which one comes next.
 */

/** Design §11.3's wording rules, applied to the list. */
const ROUTE_STATUS: Record<
  AuthorizationRouteSummary['status'],
  { label: string; tone: string; title: string }
> = {
  UNCONFIGURED: {
    label: 'Unconfigured',
    tone: 'bg-muted text-muted-foreground',
    title: 'No expected route is configured for this request type. This is not a problem.',
  },
  INTEGRITY_GAP: {
    label: 'Not shown',
    tone: 'bg-muted text-muted-foreground',
    title: 'The recorded journey could not be self-verified, so no expected route is shown.',
  },
  IN_PROGRESS: {
    label: 'In progress',
    tone: 'bg-muted text-muted-foreground',
    title: 'No step of the configured route has been reached yet.',
  },
  ON_ROUTE: {
    label: 'On route',
    tone: 'bg-green-500/10 text-green-700',
    title: 'Every recorded movement so far matches the configured expected route.',
  },
  DEVIATED: {
    label: 'Deviated',
    tone: 'bg-amber-500/10 text-amber-700',
    title: 'A recorded movement did not match the configured route. A deviation is not an error.',
  },
  COMPLETE: {
    label: 'Complete',
    tone: 'bg-green-500/10 text-green-700',
    title: 'A terminal step of the configured route was reached.',
  },
};

const GATE_LABEL: Record<AuthorizationRouteSummary['customerGate'], string | null> = {
  NONE: null,
  REACHED: 'Customer gate reached',
  NOT_REACHED: 'Customer gate not reached',
  SKIPPED_MANDATORY: 'Mandatory customer gate not reached',
};

export function RouteStatusCell({ route }: { route: AuthorizationRouteSummary }) {
  const status = ROUTE_STATUS[route.status];
  const gate = GATE_LABEL[route.customerGate];

  return (
    <div
      className="flex flex-col gap-1"
      data-testid="route-status-cell"
      data-route-status={route.status}
      data-customer-gate={route.customerGate}
      data-deviations={route.deviationCount}
      data-overrides={route.overrideCount}
    >
      <span
        title={status.title}
        className={`w-fit rounded-full px-2 py-0.5 text-xs font-medium ${status.tone}`}
      >
        {status.label}
        {route.versionNumber !== null && (
          <span className="ml-1 font-normal opacity-70">v{route.versionNumber}</span>
        )}
      </span>

      {/* Plain labelled counts — never a sequence, never a diagram (§12.3). */}
      {route.deviationCount > 0 && (
        <span className="text-[11px] text-muted-foreground">
          {route.deviationCount} deviation{route.deviationCount === 1 ? '' : 's'}
        </span>
      )}
      {/* Structurally 0 until 5G1 exists; rendered only if one ever appears. */}
      {route.overrideCount > 0 && (
        <span className="text-[11px] text-muted-foreground">
          {route.overrideCount} override{route.overrideCount === 1 ? '' : 's'}
        </span>
      )}
      {gate && <span className="text-[11px] text-muted-foreground">{gate}</span>}
    </div>
  );
}
